import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { AIIcon } from './AIIcon';
import type { TrackGenerationParams } from '@/store/slices/aiSlice';

interface TrackQuestionnaireProps {
  onSubmit: (params: TrackGenerationParams) => void;
  onCancel: () => void;
}

const TIME_OPTIONS: { label: string; value: TrackGenerationParams['timeAvailable'] }[] = [
  { label: '2 weeks', value: '2-weeks' },
  { label: '1 month', value: '1-month' },
  { label: '2 months', value: '2-months' },
  { label: '3 months', value: '3-months' },
  { label: '6 months', value: '6-months' },
  { label: '1 year', value: '1-year' },
];

const LEVEL_OPTIONS: { label: string; value: TrackGenerationParams['currentLevel'] }[] = [
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
];

const TASKS_PRESETS = [3, 5, 10, 15];

export function TrackQuestionnaire({ onSubmit, onCancel }: TrackQuestionnaireProps) {
  const [timeAvailable, setTimeAvailable] = useState<TrackGenerationParams['timeAvailable'] | null>(null);
  const [currentLevel, setCurrentLevel] = useState<TrackGenerationParams['currentLevel'] | null>(null);
  const [tasksPerDay, setTasksPerDay] = useState<number | null>(null);
  const [customTasks, setCustomTasks] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const isValid = timeAvailable && currentLevel && (tasksPerDay !== null);

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      timeAvailable: timeAvailable!,
      currentLevel: currentLevel!,
      tasksPerDay: tasksPerDay!,
    });
  };

  const handleCustomTasksChange = (val: string) => {
    setCustomTasks(val);
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 1 && num <= 30) {
      setTasksPerDay(num);
    } else {
      setTasksPerDay(null);
    }
  };

  const handlePresetTaskClick = (n: number) => {
    setShowCustomInput(false);
    setCustomTasks('');
    setTasksPerDay(n);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex gap-3"
    >
      <AIIcon size={32} className="flex-shrink-0" static />
      <div className="flex-1 max-w-[85%] bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-gray-500" />
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Before I generate your track, a few quick questions:
          </p>
        </div>

        {/* Time Available */}
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            How much time do you have?
          </p>
          <div className="flex flex-wrap gap-1.5">
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTimeAvailable(opt.value)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-all',
                  timeAvailable === opt.value
                    ? 'bg-violet-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Current Level */}
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            What's your current level?
          </p>
          <div className="flex flex-wrap gap-1.5">
            {LEVEL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setCurrentLevel(opt.value)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-all',
                  currentLevel === opt.value
                    ? 'bg-violet-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tasks Per Day */}
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            Tasks per day?
          </p>
          <div className="flex flex-wrap gap-1.5 items-center">
            {TASKS_PRESETS.map((n) => (
              <button
                key={n}
                onClick={() => handlePresetTaskClick(n)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-all',
                  tasksPerDay === n && !showCustomInput
                    ? 'bg-violet-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                )}
              >
                {n}
              </button>
            ))}
            {showCustomInput ? (
              <input
                type="number"
                min={1}
                max={30}
                value={customTasks}
                onChange={(e) => handleCustomTasksChange(e.target.value)}
                placeholder="1-30"
                autoFocus
                className="w-16 px-2 py-1 rounded-full text-xs font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-violet-500/40 text-center"
              />
            ) : (
              <button
                onClick={() => {
                  setShowCustomInput(true);
                  setTasksPerDay(null);
                }}
                className="px-3 py-1 rounded-full text-xs font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-violet-50 dark:hover:bg-gray-600 border border-dashed border-gray-300 dark:border-gray-600"
              >
                Custom
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-3 h-3" />
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid}
            className={cn(
              'flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-medium transition-all',
              isValid
                ? 'bg-violet-600 hover:bg-violet-700 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
            )}
          >
            <Sparkles className="w-3 h-3" />
            Generate Track
          </button>
        </div>
      </div>
    </motion.div>
  );
}
